import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import codificacionApi from "../../api/masters/codificacionApi";
import { apiGetConsecutivos } from "../../api/consecutivoApi";

const emptyForm = {
  id: "",
  consecutivo: "",
  cod_API: "",
  API: "",
  unidades: "",
};

const emptyOpcion = {
  IdLista: "",
  Valor: "",
  orden: 1,
};

export const useCodificacion = () => {
  const [codificaciones, setCodificaciones] = useState([]);
  const [consecutivos, setConsecutivos] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [opciones, setOpciones] = useState([]);
  const [newOpcion, setNewOpcion] = useState(emptyOpcion);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOpcion, setSavingOpcion] = useState(false);

  const loadConsecutivos = async () => {
    try {
      const res = await apiGetConsecutivos();
      if (res?.data) {
        setConsecutivos(res.data);
      }
    } catch {
      // Ignorar fallo no crítico
    }
  };

  const loadCodificaciones = async () => {
    setLoading(true);
    try {
      const res = await codificacionApi.getAll();
      setCodificaciones(res?.data || []);
    } catch {
      toast.error("Error al cargar las codificaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConsecutivos();
    loadCodificaciones();
  }, []);

  const handleSelect = async (id) => {
    setSelectedId(id);
    try {
      const res = await codificacionApi.getById(id);
      if (res?.success && res.data) {
        const item = res.data;
        setForm({
          id: item.id,
          consecutivo: item.consecutivo ?? "",
          cod_API: item.cod_API ?? "",
          API: item.API ?? "",
          unidades: item.unidades ?? "",
        });
        setOpciones(item.opciones || []);
        // Sugerir siguiente orden para nueva opción
        const nextOrder = (item.opciones?.length || 0) + 1;
        setNewOpcion({ IdLista: "", Valor: "", orden: nextOrder });
      } else {
        toast.error("No se pudo cargar la codificación seleccionada");
      }
    } catch {
      toast.error("Error al consultar el registro");
    }
  };

  const handleNew = () => {
    setSelectedId(null);
    setForm(emptyForm);
    setOpciones([]);
    setNewOpcion(emptyOpcion);
  };

  const handleSave = async () => {
    if (!form.consecutivo) {
      toast.warning("Debe seleccionar un Consecutivo");
      return;
    }

    setSaving(true);
    try {
      const res = await codificacionApi.save(form);
      if (res?.success) {
        toast.success(form.id ? "Codificación actualizada" : "Codificación creada exitosamente");
        await loadCodificaciones();
        if (!form.id && res.data?.lastId) {
          handleSelect(res.data.lastId);
        } else if (form.id) {
          handleSelect(form.id);
        }
      } else {
        toast.error(res?.message || "Error al guardar la codificación");
      }
    } catch {
      toast.error("Error interno al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      const res = await codificacionApi.delete(id);
      if (res?.success) {
        toast.success("Codificación eliminada");
        if (selectedId === id) {
          handleNew();
        }
        await loadCodificaciones();
      } else {
        toast.error(res?.message || "Error al eliminar");
      }
    } catch {
      toast.error("Error al procesar la eliminación");
    }
  };

  const handleAddOpcion = async () => {
    if (!form.id) {
      toast.warning("Primero debe guardar la cabecera de la Codificación");
      return;
    }
    if (newOpcion.IdLista === "" || newOpcion.IdLista === null) {
      toast.warning("El código de opción (IdLista) es obligatorio");
      return;
    }
    if (!newOpcion.Valor?.trim()) {
      toast.warning("La descripción del valor es obligatoria");
      return;
    }

    setSavingOpcion(true);
    try {
      const payload = {
        IdCodificacion: form.id,
        IdLista: newOpcion.IdLista,
        Valor: newOpcion.Valor.trim(),
        orden: newOpcion.orden || opciones.length + 1,
      };
      const res = await codificacionApi.addValor(payload);
      if (res?.success) {
        toast.success("Opción agregada");
        // Recargar datos y opciones
        await handleSelect(form.id);
        await loadCodificaciones();
      } else {
        toast.error(res?.message || "Error al agregar la opción");
      }
    } catch {
      toast.error("Error al guardar la opción");
    } finally {
      setSavingOpcion(false);
    }
  };

  const handleDeleteOpcion = async (idOpcion) => {
    if (!idOpcion) return;
    try {
      const res = await codificacionApi.deleteValor(idOpcion);
      if (res?.success) {
        toast.success("Opción removida");
        setOpciones((prev) => prev.filter((o) => (o.Id || o.id) !== idOpcion));
        await loadCodificaciones();
      } else {
        toast.error(res?.message || "Error al eliminar la opción");
      }
    } catch {
      toast.error("Error al eliminar la opción");
    }
  };

  const filteredCodificaciones = codificaciones.filter((c) => {
    const q = search.toLowerCase();
    const consMatch = String(c.consecutivo || "").includes(q);
    const apiMatch = (c.API || "").toLowerCase().includes(q);
    const codApiMatch = String(c.cod_API || "").includes(q);
    return consMatch || apiMatch || codApiMatch;
  });

  return {
    codificaciones: filteredCodificaciones,
    allCodificaciones: codificaciones,
    consecutivos,
    selectedId,
    form,
    setForm,
    opciones,
    newOpcion,
    setNewOpcion,
    search,
    setSearch,
    loading,
    saving,
    savingOpcion,
    handleSelect,
    handleNew,
    handleSave,
    handleDelete,
    handleAddOpcion,
    handleDeleteOpcion,
  };
};

export default useCodificacion;

